import { AlertTriangle, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SegmentedControl } from "@/components/calendar/plan/plan-parts";
import { SlotTag, TimeSelect } from "@/components/calendar/plan/slot-list";
import { DarkPill, OutlinePill, calendarDialogClass, calendarOverlayClass } from "@/components/calendar/ui";
import {
  CHECK_WEEKS,
  DAY_END_MIN,
  DAY_START_MIN,
  addDays,
  dayKey,
  formatClock,
  formatDay,
  formatMonthDay,
  formatTime,
  parseDayKey,
  rhythmDaysLabel,
  weekdayShort,
  type IfBusy,
  type Rhythm,
  type Weekday,
} from "@/lib/calendar";
import type { Interval, RhythmDate, RhythmSuggestion } from "@/lib/calendar-slots";
import { cn } from "@/lib/utils";

const MONDAY_FIRST: Weekday[] = [1, 2, 3, 4, 5, 6, 0];

export const suggestionKey = (s: Pick<RhythmSuggestion, "days" | "minutes">) => `${[...s.days].sort().join("")}@${s.minutes}`;

/** J08 middle column: days, time, start and length, then suggested rhythms checked against busy times. */
export function RhythmBuilder({
  rhythm,
  onChange,
  fit,
  suggestions,
  checking,
  startOptions,
}: {
  rhythm: Rhythm;
  onChange: (patch: Partial<Rhythm>) => void;
  fit: Record<Weekday, { free: number; total: number } | null>;
  suggestions: RhythmSuggestion[];
  checking: boolean;
  startOptions: Date[];
}) {
  const weeks = rhythm.weeks ?? CHECK_WEEKS;
  const toggleDay = (d: Weekday) => {
    const has = rhythm.days.includes(d);
    if (has && rhythm.days.length === 1) return; // a rhythm needs at least one day
    onChange({ days: has ? rhythm.days.filter((x) => x !== d) : [...rhythm.days, d] });
  };
  const currentKey = suggestionKey(rhythm);
  const dailyPreset = rhythm.days.length === 7 ? "every" : rhythm.days.length === 5 && rhythm.days.every((d) => d >= 1 && d <= 5) ? "weekdays" : "custom";

  return (
    <div>
      <h2 className="text-[17px] font-semibold text-foreground">Set your rhythm</h2>

      {rhythm.frequency === "daily" && (
        <SegmentedControl
          className="mt-4"
          label="Which days, daily"
          value={dailyPreset}
          onChange={(v) => {
            if (v === "every") onChange({ days: [0, 1, 2, 3, 4, 5, 6] });
            if (v === "weekdays") onChange({ days: [1, 2, 3, 4, 5] });
          }}
          options={[
            { value: "every", label: "Every day" },
            { value: "weekdays", label: "Weekdays" },
            { value: "custom", label: "Custom" },
          ]}
        />
      )}

      <div className="mt-4 flex items-baseline justify-between gap-3">
        <p className="text-[14px] font-medium text-foreground">Which days</p>
        <p className="text-xs text-muted-foreground">
          {checking ? "Checking…" : `Free at ${formatTime(rhythm.minutes)}, next ${weeks} weeks`}
        </p>
      </div>
      <div className="mt-2.5 grid grid-cols-7 gap-1.5">
        {MONDAY_FIRST.map((d) => {
          const on = rhythm.days.includes(d);
          const f = fit[d];
          return (
            <button
              key={d}
              type="button"
              aria-pressed={on}
              onClick={() => toggleDay(d)}
              className={cn(
                "flex h-[52px] flex-col items-center justify-center rounded-[12px] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                on ? "border-foreground bg-foreground text-white" : "border-foreground/[0.12] text-foreground hover:border-foreground/25",
              )}
            >
              <span className="text-[14px]">{weekdayShort(d)}</span>
              {f && <span className={cn("text-[10.5px] tabular-nums", on ? "text-white/70" : "text-muted-foreground")}>{f.free}/{f.total}</span>}
            </button>
          );
        })}
      </div>

      <div className="mt-5 grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.25fr)] gap-3">
        <label className="block">
          <span className="text-[13px] text-muted-foreground">Time</span>
          <TimeSelect
            label="Rhythm time"
            value={rhythm.minutes}
            onChange={(m) => onChange({ minutes: m })}
            durationMin={rhythm.durationMin}
            className="mt-1.5 flex h-12 w-full"
          />
        </label>
        <label className="block">
          <span className="text-[13px] text-muted-foreground">Starting</span>
          <span className="relative mt-1.5 flex h-12 items-center gap-2 rounded-[12px] border border-foreground/[0.12] bg-white px-3 text-[14px] text-foreground focus-within:ring-2 focus-within:ring-primary/40">
            <CalendarDays className="h-4 w-4 text-foreground/60" />
            {formatDay(parseDayKey(rhythm.startDate))}
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-foreground/50" />
            <select
              aria-label="Starting date"
              value={rhythm.startDate}
              onChange={(e) => onChange({ startDate: e.target.value })}
              className="absolute inset-0 cursor-pointer opacity-0"
            >
              {startOptions.map((d) => (
                <option key={dayKey(d)} value={dayKey(d)}>
                  {formatDay(d)}
                </option>
              ))}
            </select>
          </span>
        </label>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[13px] text-muted-foreground">Repeat for</p>
        <SegmentedControl
          className="w-[220px]"
          itemClassName="h-9 text-[13.5px]"
          label="Repeat for"
          value={rhythm.weeks === null ? "ongoing" : String(rhythm.weeks)}
          onChange={(v) => onChange({ weeks: v === "ongoing" ? null : Number(v) })}
          options={[
            { value: "4", label: "4 wk" },
            { value: "8", label: "8 wk" },
            { value: "ongoing", label: "Ongoing" },
          ]}
        />
      </div>

      <div className="my-5 h-px bg-foreground/[0.07]" />

      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-foreground">Suggested rhythms</h3>
        <p className="text-xs text-muted-foreground">Checked against the next {weeks} weeks</p>
      </div>
      <div role="radiogroup" aria-label="Suggested rhythms" className="mt-3 space-y-2.5">
        {checking
          ? Array.from({ length: 3 }, (_, i) => <div key={i} aria-hidden className="h-[66px] animate-pulse rounded-[14px] bg-foreground/[0.04]" />)
          : suggestions.map((s, i) => {
              const key = suggestionKey(s);
              const on = key === currentKey;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onChange({ days: s.days, minutes: s.minutes })}
                  className={cn(
                    "flex w-full items-center gap-3.5 rounded-[14px] border px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    on ? "border-primary/55 bg-primary/[0.05] shadow-[0_0_0_3px_rgba(110,83,172,0.07)]" : "border-foreground/[0.1] hover:border-foreground/20",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] bg-white",
                      on ? "border-primary" : "border-foreground/35",
                    )}
                  >
                    {on && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[16px] font-medium text-foreground">
                        {rhythmDaysLabel({ frequency: rhythm.frequency, days: s.days })} · {formatTime(s.minutes)}
                      </span>
                      {i === 0 && <SlotTag kind="best" />}
                    </span>
                    <span className="mt-0.5 block text-[12.5px] text-muted-foreground">{s.reason}</span>
                  </span>
                </button>
              );
            })}
      </div>
    </div>
  );
}

const WEEK_PX_PER_MIN = 0.4;

/** J08 right column: the rhythm against a week of busy times, then every date and the busy rule. */
export function WeekView({
  weekStart,
  onWeek,
  minWeekStart,
  busyFor,
  rhythm,
  dates,
  onIfBusy,
}: {
  /** Monday of the week shown. */
  weekStart: Date;
  onWeek: (d: Date) => void;
  minWeekStart: Date;
  busyFor: (d: Date) => Interval[] | null;
  rhythm: Rhythm;
  dates: RhythmDate[];
  onIfBusy: (v: IfBusy) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const height = (DAY_END_MIN - DAY_START_MIN) * WEEK_PX_PER_MIN;
  const byDay = new Map(dates.map((d) => [d.day, d]));
  const y = (m: number) => (Math.max(DAY_START_MIN, Math.min(DAY_END_MIN, m)) - DAY_START_MIN) * WEEK_PX_PER_MIN;
  const shown = dates.length;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[17px] font-semibold text-foreground">
          Your week · {formatMonthDay(days[0])} – {days[6].getMonth() === days[0].getMonth() ? days[6].getDate() : formatMonthDay(days[6])}
        </h2>
        <div className="flex">
          <button
            type="button"
            aria-label="Previous week"
            disabled={weekStart <= minWeekStart}
            onClick={() => onWeek(addDays(weekStart, -7))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground/70 hover:bg-foreground/[0.05] disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next week"
            onClick={() => onWeek(addDays(weekStart, 7))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground/70 hover:bg-foreground/[0.05]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="mt-0.5 text-[12.5px] text-muted-foreground">Busy times only. Your rhythm is shown in violet.</p>

      <div className="mt-4 flex">
        <div className="relative mt-6 w-8 shrink-0" style={{ height }}>
          {[7, 9, 11, 13, 15, 17, 19, 21].map((h) => (
            <span key={h} className="absolute -translate-y-1/2 text-[10px] text-muted-foreground" style={{ top: y(h * 60) }}>
              {h % 12 === 0 ? 12 : h % 12}
              {h < 12 ? "a" : "p"}
            </span>
          ))}
        </div>
        <div className="grid flex-1 grid-cols-7 gap-1">
          {days.map((d) => {
            const key = dayKey(d);
            const date = byDay.get(key);
            const selectedDay = rhythm.days.includes(d.getDay() as Weekday);
            const busy = busyFor(d) ?? [];
            return (
              <div key={key} className="min-w-0">
                <p className={cn("mb-2 truncate text-center text-[11px]", selectedDay ? "font-medium text-primary" : "text-muted-foreground")}>
                  {weekdayShort(d.getDay())} {d.getDate()}
                </p>
                <div className={cn("relative rounded-[8px]", selectedDay ? "bg-primary/[0.07]" : "bg-foreground/[0.025]")} style={{ height }}>
                  {busy.map((b) =>
                    b.end <= DAY_START_MIN || b.start >= DAY_END_MIN ? null : (
                      <span
                        key={`${b.start}-${b.end}`}
                        aria-hidden
                        className="absolute inset-x-1 rounded-[5px] bg-[#e3ded8]"
                        style={{ top: y(b.start), height: Math.max(4, y(b.end) - y(b.start)) }}
                      />
                    ),
                  )}
                  {date && date.status !== "skipped" && (
                    <span
                      title={`${formatTime(date.start)}${date.status === "moved" ? " · moved" : ""}`}
                      className={cn(
                        "absolute inset-x-1 rounded-full",
                        date.status === "moved" ? "bg-[#e0b04a]" : date.status === "overlaps" ? "bg-[#d9545e]" : "bg-primary",
                      )}
                      style={{ top: y(date.start), height: Math.max(6, rhythm.durationMin * WEEK_PX_PER_MIN) }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-6 text-[14px] font-medium text-foreground">
        {rhythm.weeks === null ? `Next ${CHECK_WEEKS} weeks` : `Next ${rhythm.weeks} weeks`} · {shown} {shown === 1 ? "date" : "dates"}
      </p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {dates.map((d) => {
          const day = parseDayKey(d.day);
          return (
            <span
              key={d.day}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-1 text-[12px] tabular-nums",
                d.status === "moved" && "bg-[#fbf1d6] text-[#86621a] ring-1 ring-inset ring-[#ecd28c]",
                d.status === "skipped" && "bg-foreground/[0.04] text-muted-foreground line-through",
                d.status === "overlaps" && "bg-[#fbe6e6] text-[#ad3640]",
                (d.status === "free" || d.status === "unchecked") && "bg-primary/10 text-primary",
              )}
            >
              {d.status === "moved" && <AlertTriangle className="h-3 w-3" />}
              {formatMonthDay(day)}
              {d.status === "moved" && ` → ${formatClock(d.start)}`}
            </span>
          );
        })}
      </div>

      <p className="mt-5 text-[14px] font-medium text-foreground">If a date is busy</p>
      <SegmentedControl
        className="mt-2.5"
        itemClassName="text-[13px] px-1"
        label="If a date is busy"
        value={rhythm.ifBusy}
        onChange={onIfBusy}
        options={[
          { value: "move", label: "Move to next free time" },
          { value: "skip", label: "Skip that date" },
          { value: "keep", label: "Add anyway" },
        ]}
      />
    </div>
  );
}

/** J09 · every date before anything is added. Tapping a date skips it (or brings it back). */
export function RhythmReviewDialog({
  open,
  onOpenChange,
  stepText,
  rhythmText,
  datesText,
  calendarText,
  dates,
  onToggleSkip,
  onConfirm,
  confirmLabel,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stepText: string;
  rhythmText: string;
  datesText: string;
  calendarText: string;
  dates: RhythmDate[];
  onToggleSkip: (day: string) => void;
  onConfirm: () => void;
  confirmLabel: string;
  pending: boolean;
}) {
  const moved = dates.filter((d) => d.status === "moved");
  const ruleSkipped = dates.filter((d) => d.status === "skipped" && !d.byPerson);
  const personSkipped = dates.filter((d) => d.byPerson);
  const adding = dates.length - ruleSkipped.length - personSkipped.length;
  const name = (d: RhythmDate) => formatDay(parseDayKey(d.day));
  const note = [
    ...moved.map((d) => `${name(d)} is busy at ${formatClock(d.planned)}, so it moves to ${formatClock(d.start)}, the next free time.`),
    ruleSkipped.length ? `${ruleSkipped.map(name).join(", ")} ${ruleSkipped.length === 1 ? "has" : "have"} no free time, so ${ruleSkipped.length === 1 ? "it's" : "they're"} skipped.` : "",
    personSkipped.length ? `You skipped ${personSkipped.map(name).join(", ")}.` : "",
    `${adding} ${adding === 1 ? "event" : "events"} will be added.`,
  ]
    .filter(Boolean)
    .join(" ");

  const rows = [
    ["Next Step", stepText],
    ["Rhythm", rhythmText],
    ["Dates", datesText],
    ["Calendar", calendarText],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideClose overlayClassName={calendarOverlayClass} className={cn(calendarDialogClass, "sm:max-w-[620px]")}>
        <div className="px-6 pb-6 pt-7 sm:px-8 sm:pt-8">
          <DialogTitle className="font-display text-[26px] font-semibold tracking-[-0.02em] text-foreground">Review your routine</DialogTitle>
          <dl className="mt-4 overflow-hidden rounded-2xl border border-foreground/[0.08]">
            {rows.map(([k, v], i) => (
              <div key={k} className={cn("grid grid-cols-[96px_minmax(0,1fr)] gap-3 px-4 py-3 text-[14px]", i > 0 && "border-t border-foreground/[0.06]")}>
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-foreground [overflow-wrap:anywhere]">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-5 flex items-baseline justify-between">
            <p className="text-[15px] font-semibold text-foreground">Dates</p>
            <p className="text-xs text-muted-foreground">Tap a date to skip it</p>
          </div>
          <div className="mt-3 grid max-h-[300px] grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-4">
            {dates.map((d) => {
              const skipped = d.status === "skipped";
              return (
                <button
                  key={d.day}
                  type="button"
                  aria-pressed={!skipped}
                  aria-label={`${name(d)}, ${skipped ? "skipped" : formatTime(d.start)}`}
                  onClick={() => onToggleSkip(d.day)}
                  className={cn(
                    "rounded-[12px] border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    d.status === "moved" && "border-[#ecd28c] bg-[#fdf6e1]",
                    d.status === "overlaps" && "border-[#efbcc0] bg-[#fdf1f1]",
                    skipped && "border-foreground/[0.06] bg-foreground/[0.04]",
                    (d.status === "free" || d.status === "unchecked") && "border-foreground/[0.1] hover:border-foreground/25",
                  )}
                >
                  <span className={cn("block text-[13.5px] text-foreground", skipped && "text-muted-foreground line-through")}>{name(d)}</span>
                  <span className="block text-[12px] text-muted-foreground">
                    {skipped ? (d.byPerson ? "Skipped" : "Skipped · no free time") : d.status === "moved" ? `${formatClock(d.start)} · moved` : d.status === "overlaps" ? `${formatClock(d.start)} · overlaps` : formatClock(d.start)}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="mt-4 flex gap-2.5 rounded-2xl bg-[#f4f1ec] px-4 py-3 text-[13px] leading-relaxed text-foreground/80">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            {note}
          </p>

          <div className="mt-6 flex justify-end gap-3">
            <OutlinePill onClick={() => onOpenChange(false)}>Back</OutlinePill>
            <DarkPill loading={pending} disabled={adding === 0} onClick={onConfirm}>
              {!pending && <CalendarDays className="h-4 w-4" />}
              {confirmLabel.replace("{n}", String(adding))}
            </DarkPill>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
